
import {
  defineComponent,
  markRaw,
} from 'vue';
import {
  ContentBlock,
  ContentTable,
  Matomo,
  SiteRef,
  translate,
  Notification,
  MatomoUrl,
  Site,
  useExternalPluginComponent,
  Tooltips,
} from 'CoreHome';
import { Field } from 'CorePluginsAdmin';
import { getDisplayApiKey } from '../utilities';
import SearchEngineNavigation from '../Navigation/Navigation.vue';
import WebsitesAvailableModal from '../components/WebsitesAvailableModal.vue';
import CredentialsErrorBlock from '../components/CredentialsErrorBlock.vue';

interface GoogleConfigState {
  removeAccountConfigName: string;
  removeAccountName: string;
  isAddingMeasurable: boolean;
  currentSiteToAdd: SiteRef;
  googleAccountAndUrlToAdd: string|null;
  googleTypesToAdd: string[];
  clientFile: unknown;
  clientText: string;
}

interface GoogleAccount {
  name: string;
  apiKey: string;
  urls: Record<string, string>;
}

interface ConfiguredGoogleInfo {
  googleSearchConsoleUrl: string;
}

interface ComponentExtension {
  plugin: string;
  component: string;
}

interface ComponentExtensionWithResolvedComponent extends ComponentExtension {
  resolvedComponent: unknown;
}

interface ConfigureConnectionRadioOption {
  connectAccounts: string;
  manual: string;
}

interface ConfigureConnectionProps {
  baseDomain: string;
  baseUrl: string;
  manualConfigNonce: string;
  manualActionUrl: string;
  primaryText: string;
  radioOptions: ConfigureConnectionRadioOption[];
  manualConfigText: string;
  connectAccountsUrl: string;
  connectAccountsBtnText: string;
  authUrl: string;
  unlinkUrl: string;
  strategy: string;
  connectedWith: string;
  authorizedJsOrigin: string;
  authorizedRedirectUrl: string;
  faqUrl: string;
}

export default defineComponent({
  props: {
    configuredMeasurables: {
      type: Object,
      required: true,
    },
    isClientConfigured: Boolean,
    isClientConfigurable: Boolean,
    isOAuthConfigured: Boolean,
    clientId: String,
    clientSecret: String,
    accounts: {
      type: Object,
      required: true,
    },
    sitesInfos: {
      type: Object,
      required: true,
    },
    currentSite: {
      type: Object,
      required: true,
    },
    urlOptions: {
      type: [Object, Array],
      required: true,
    },
    hasOAuthError: [String, Boolean],
    authNonce: {
      type: String,
      required: true,
    },
    formNonce: String,
    addGoogleSiteConfigNonce: String,
    removeGoogleSiteConfigNonce: String,
    removeGoogleAccountNonce: String,
    countOfAccountsWithAccess: Number,
    userIsSuperUser: String,
    extensions: Array,
    removeConfigUrl: String,
    hasCredentialsError: Boolean,
    resetCredentialsUrl: String,
    providerName: {
      type: String,
      default: '',
    },
    configureConnectionProps: {
      type: Object,
      required: true,
    },
  },
  components: {
    ContentBlock,
    CredentialsErrorBlock,
    Field,
    Notification,
    SearchEngineNavigation,
    WebsitesAvailableModal,
  },
  directives: {
    ContentTable,
    Tooltips,
  },
  data(): GoogleConfigState {
    return {
      removeAccountConfigName: '',
      removeAccountName: '',
      isAddingMeasurable: false,
      currentSiteToAdd: this.currentSite as SiteRef,
      googleAccountAndUrlToAdd: null,
      googleTypesToAdd: ['web'],
      clientFile: null,
      clientText: '',
    };
  },
  methods: {
    removeAccountConfig(siteId: string|number, event: Event) {
      const siteInfos = this.sitesInfos as Record<string|number, Site>;
      this.removeAccountConfigName = siteInfos[siteId].name;

      Matomo.helper.modalConfirm(this.$refs.confirmRemoveAccountConfig as HTMLElement, {
        yes() {
          (event.target as HTMLFormElement).submit();
        },
      });
    },
    removeAccount(account: GoogleAccount, event: Event) {
      this.removeAccountName = account.name;

      Matomo.helper.modalConfirm(
        this.$refs.confirmDeleteAccount as HTMLElement,
        {
          yes() {
            (event.target as HTMLFormElement).submit();
          },
        },
      );
    },
    accountHasAvailableSites(account: GoogleAccount) {
      const siteAccessLevels = ['siteOwner', 'siteFullUser', 'siteRestrictedUser'];
      return Object.values(account.urls).some(
        (siteAccess) => siteAccessLevels.indexOf(siteAccess) !== -1,
      );
    },
    getAvailableWebsites(account: GoogleAccount): string[] {
      return Object.entries(account.urls)
        .filter(([, level]) => level !== 'siteUnverifiedUser')
        .map(([url]) => url.replaceAll('sc-domain:', ''))
        .sort((first, second) => first.localeCompare(second, undefined, { sensitivity: 'base' }));
    },
    getUnverifiedWebsites(account: GoogleAccount): string[] {
      return Object.entries(account.urls)
        .filter(([, accessLevel]) => accessLevel === 'siteUnverifiedUser')
        .map(([url]) => url.replaceAll('sc-domain:', ''))
        .sort((first, second) => first.localeCompare(second, undefined, { sensitivity: 'base' }));
    },
    getAdditionalWebsitesLinkLabel(websites: string[]): string {
      return `+${websites.length - 1}`;
    },
  },
  computed: {
    unverifiedWebsitesModalTitle() {
      return translate('SearchEngineKeywordsPerformance_UnverifiedWebsites');
    },
    configuredMeasurablesToDisplay() {
      const entries = Object.entries(
        this.configuredMeasurables as Record<string | number, ConfiguredGoogleInfo>,
      );

      return Object.fromEntries(
        entries.filter(([, config]) => {
          const [account] = config.googleSearchConsoleUrl.split('##');
          return !!this.accounts[account];
        }).map(([siteId, config]) => {
          const [account, url] = config.googleSearchConsoleUrl.split('##');
          const { apiKey } = this.accounts[account];

          return [
            siteId,
            {
              ...config,
              account,
              url,
              apiKeyDisplay: getDisplayApiKey(apiKey),
            },
          ];
        }),
      );
    },
    accountsToDisplay() {
      const asArray = Object.entries(this.accounts);
      const filtered = asArray.filter(([, value]) => value.hasAccess);

      return Object.fromEntries(filtered);
    },
    googleTypeOptions() {
      return {
        web: translate('SearchEngineKeywordsPerformance_KeywordTypeWeb'),
        image: translate('SearchEngineKeywordsPerformance_KeywordTypeImage'),
        video: translate('SearchEngineKeywordsPerformance_KeywordTypeVideo'),
        news: translate('SearchEngineKeywordsPerformance_KeywordTypeNews'),
      };
    },
    forwardToAuthUrl() {
      return `?${MatomoUrl.stringify({
        ...MatomoUrl.urlParsed.value,
        action: 'forwardToAuth',
      })}`;
    },
    extensionEntries() {
      return this.extensions as Array<ComponentExtension>;
    },
    resolvedComponentExtensions() {
      const entries = this.extensionEntries as Array<ComponentExtension>;

      return markRaw(entries.map((entry) => ({
        ...entry,
        resolvedComponent: useExternalPluginComponent(entry.plugin, entry.component),
      }))) as Array<ComponentExtensionWithResolvedComponent>;
    },
    isCloudAppSetupCard() {
      return this.extensionEntries.some((entry) => entry.plugin === 'ConnectAccounts');
    },
    showCredentialsError() {
      return !!this.userIsSuperUser && !!this.hasCredentialsError;
    },
    showClientConfiguration() {
      return !!this.userIsSuperUser
        && !!this.isClientConfigured
        && !!this.isClientConfigurable
        && (!!this.clientId || !!this.clientSecret);
    },
    showSetupExtensions() {
      return this.userIsSuperUser;
    },
    hasSetupCard() {
      return this.showClientConfiguration
        || (this.showSetupExtensions && this.extensionEntries.length > 0);
    },
    setupCardTitle() {
      if (this.isCloudAppSetupCard) {
        return this.extensionEntries.some((entry) => entry.plugin === 'ConnectAccounts'
          && entry.component === 'PluginConnectedNotice')
          ? translate('ConnectAccounts_ConnectedWithHeader')
          : translate('SearchEngineKeywordsPerformance_ConnectUsingMatomoCloudApp');
      }

      if (this.showClientConfiguration) {
        return translate('SearchEngineKeywordsPerformance_OAuthClientConfig');
      }

      return translate('SearchEngineKeywordsPerformance_GoogleConfigurationTitle');
    },
    connectedWithBody() {
      return translate('ConnectAccounts_ConnectedWithBody', [this.configConnectProps.connectedWith]);
    },
    reAuthorizeBody() {
      return translate('ConnectAccounts_ReAuthorizeBody', [this.configConnectProps.connectedWith]);
    },
    reAuthorizeBtnText() {
      return translate('ConnectAccounts_ReAuthorizeBtnText', [this.configConnectProps.connectedWith]);
    },
    configConnectProps() {
      return this.configureConnectionProps as ConfigureConnectionProps;
    },
  },
});
